import * as React from "react";
import { useEditor } from "@grapesjs/react";
// import { mdiEyeOffOutline, mdiEyeOutline, mdiMenuDown } from '@mdi/js';
// import Icon from '@mdi/react';
import type { Component } from "grapesjs";
import debounce from "lodash/debounce";
import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { Box, Stack, Typography, useTheme } from "@mui/material";
// import { MAIN_BORDER_COLOR, cx } from './common';

export declare interface ILightningLandersLayoutItemProps
  extends React.HTMLProps<HTMLDivElement> {
  component: Component;
  level: number;
  draggingCmp?: Component;
  dragParent?: Component;
}

export default function LightningLandersLayoutItem({
  component,
  draggingCmp,
  dragParent,
  ...props
}: ILightningLandersLayoutItemProps) {
  const theme = useTheme();
  const editor = useEditor();
  const { Layers } = editor;
  const layerRef = useRef<HTMLDivElement>(null);
  const [layerData, setLayerData] = useState(Layers.getLayerData(component));
  const { open, components, visible, hovered, name, selected } = layerData;
  // const componentsIds = components.map((cmp) => cmp.getId());
  const isDragging = draggingCmp === component;
  // const cmpHash = componentsIds.join("-");
  const level = props.level + 1;
  const isHovered = hovered || dragParent === component;

  useEffect(() => {
    level === 0 && setLayerData(Layers.getLayerData(component));
    if (layerRef.current) {
      (layerRef.current as any).__cmp = component;
    }
  }, [component]);

  const LAYER_DEBOUNCE_MS = 100;
  useEffect(() => {
    const ev = Layers.events.component;
    const debouncedSetLayer = debounce(() => {
      setLayerData(Layers.getLayerData(component));
    }, LAYER_DEBOUNCE_MS);
    const up = (cmp: Component) => {
      cmp === component && debouncedSetLayer();
    };
    editor.on(ev, up);

    return () => {
      debouncedSetLayer.cancel();
      editor.off(ev, up);
    };
  }, [editor, Layers, component]);

  const cmpToRender = useMemo(() => {
    return components.map((cmp) => (
      <LightningLandersLayoutItem
        key={cmp.getId()}
        component={cmp}
        level={level}
        draggingCmp={draggingCmp}
        dragParent={dragParent}
      />
    ));
  }, [draggingCmp, dragParent, components, level]);

  const toggleOpen = (ev: MouseEvent) => {
    ev.stopPropagation();
    Layers.setLayerData(component, { open: !open });
  };

  const toggleVisibility = (ev: MouseEvent) => {
    ev.stopPropagation();
    Layers.setLayerData(component, { visible: !visible });
  };

  const select = (event: MouseEvent) => {
    event.stopPropagation();
    Layers.setLayerData(component, { selected: true }, { event });
  };

  const hover = (hovered: boolean) => {
    if (!hovered || !draggingCmp) {
      Layers.setLayerData(component, { hovered });
    }
  };

  return (
    <Box
      sx={{
        opacity: isDragging || !visible ? 0.5 : 1,
      }}
    >
      <Box
        onClick={select}
        onMouseEnter={() => hover(true)}
        onMouseLeave={() => hover(false)}
        data-layer-item
        ref={layerRef}
      >
        <Stack
          px={1}
          pt={0.5}
          pb={0}
          direction={"row"}
          alignItems="center"
          justifyContent={"space-between"}
          sx={{
            width: "100%",
            borderBottom: `1px solid ${theme.palette.divider2}`,
            backgroundColor: selected
              ? theme.palette.primary.main
              : "transparent",
            color: selected ? "white" : "initial",
            "&:hover": {
              backgroundColor: theme.palette.primary.light,
              color: "white",
            },
          }}
        >
          <Stack direction={"row"}>
            <Box
              sx={{
                marginLeft: `${level * 10}px`,
                opacity: components.length ? 100 : 0,
                cursor: "pointer",
                flexGrow: 1,
              }}
              onClick={toggleOpen}
            >
              {open ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
            </Box>
            <Typography sx={{ maxWidth: "100%" }} noWrap color={"inherit"}>
              {name}
            </Typography>
          </Stack>
          <Box
            sx={{
              opacity: isHovered || !visible ? 100 : 0,
            }}
            onClick={toggleVisibility}
          >
            {visible ? <Visibility /> : <VisibilityOff />}
          </Box>
        </Stack>
      </Box>

      {!!(open && components.length) && <div>{cmpToRender}</div>}
    </Box>
  );
}
